import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Envelope, Plus, Trash, FloppyDisk, PaperPlaneTilt, Info } from "@phosphor-icons/react";

/**
 * Manage the global list of email recipients that automatically receive
 * both the field + client PDFs and the material summary when a chantier
 * (or one of its interventions) is marked as done.
 */
export default function ReportRecipientsPanel() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ email: "", name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/report-recipients");
      setRecipients(r.data.recipients || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/report-recipients", { recipients: next });
      setRecipients(r.data.recipients || []);
      toast.success("Destinataires mis à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addOne = () => {
    const email = (draft.email || "").trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Email invalide");
      return;
    }
    if (recipients.some((r) => r.email === email)) {
      toast.error("Cet email est déjà dans la liste");
      return;
    }
    const next = [...recipients, { email, name: (draft.name || "").trim() || null }];
    setDraft({ email: "", name: "" });
    persist(next);
  };

  const removeOne = (email) => {
    persist(recipients.filter((r) => r.email !== email));
  };

  const patchOne = (email, patch) => {
    setRecipients((prev) => prev.map((r) => (r.email === email ? { ...r, ...patch } : r)));
  };

  return (
    <div className="space-y-4" data-testid="report-recipients-panel">
      <div className="rounded-2xl bg-[#005CFF]/8 border border-[#005CFF]/25 p-4 flex gap-3 items-start">
        <Info size={18} weight="duotone" className="text-[#7CB2FF] shrink-0 mt-0.5" />
        <div className="text-sm text-white/80 leading-relaxed">
          Ces adresses reçoivent <b>automatiquement</b> les <b>rapports (terrain + client) et la liste matériel</b>
          {" "}dès qu&apos;un chantier ou l&apos;une de ses interventions passe à <b>Terminé</b>.
          <div className="text-xs text-white/50 mt-1">Anti-doublon : 5 min de délai entre 2 envois auto sur le même chantier.</div>
        </div>
      </div>

      {/* Add form */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest">
          <Plus size={14} /> Ajouter un destinataire
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
          <Input
            type="email"
            placeholder="email@exemple.fr"
            value={draft.email}
            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            className="bg-white/5 border-white/10"
            data-testid="recipient-email-input"
          />
          <Input
            type="text"
            placeholder="Nom (optionnel — ex : Compta)"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className="bg-white/5 border-white/10"
            data-testid="recipient-name-input"
          />
          <Button
            onClick={addOne}
            disabled={saving}
            className="bg-[#005CFF] hover:bg-[#0047CC] h-10"
            data-testid="recipient-add-btn"
          >
            <Plus size={14} weight="bold" /> Ajouter
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden">
        <div className="p-3 border-b border-white/10 flex items-center justify-between">
          <div className="text-white/70 text-xs uppercase tracking-widest flex items-center gap-2">
            <Envelope size={14} /> Liste actuelle ({recipients.length})
          </div>
          {recipients.length > 0 && (
            <Button
              onClick={() => persist(recipients)}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 h-8 text-xs"
              data-testid="recipient-save-all"
            >
              <FloppyDisk size={12} weight="fill" /> Enregistrer les changements
            </Button>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-white/50">Chargement…</div>
        ) : recipients.length === 0 ? (
          <div className="p-6 text-center text-sm text-white/50">
            Aucun destinataire enregistré — ajoutez le premier au-dessus.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {recipients.map((r) => (
              <div key={r.email} className="p-3 grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_auto] gap-2 items-center" data-testid={`recipient-row-${r.email}`}>
                <div className="w-8 h-8 rounded-full bg-[#005CFF]/20 border border-[#005CFF]/40 flex items-center justify-center shrink-0">
                  <PaperPlaneTilt size={14} weight="duotone" className="text-[#7CB2FF]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.email}</div>
                </div>
                <Input
                  type="text"
                  value={r.name || ""}
                  onChange={(e) => patchOne(r.email, { name: e.target.value })}
                  placeholder="Nom (facultatif)"
                  className="bg-white/5 border-white/10 h-9 text-sm"
                  data-testid={`recipient-name-${r.email}`}
                />
                <button
                  onClick={() => removeOne(r.email)}
                  disabled={saving}
                  className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                  title="Retirer"
                  data-testid={`recipient-remove-${r.email}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
